import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { contactAPI, companyAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import Seo from '../components/Seo';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  useEffect(() => {
    fetchCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAPI.getInfo();
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let captchaToken = null;
      
      if (window.grecaptcha && window.grecaptcha.execute) {
        try {
          captchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'submit'});
        } catch (e) {
          console.warn('Captcha failed');
        }
      }
      
      const submitData = captchaToken ? {...formData, captcha_token: captchaToken} : formData;
      await contactAPI.create(submitData);
      
      setSubmitted(true);
      toast({ title: 'Message Sent!', description: 'We will get back to you shortly.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send message. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Message Sent!</h2>
            <p className="text-gray-600 mb-8">Thank you for reaching out. We'll get back to you as soon as possible.</p>
            <div className="flex space-x-4 justify-center">
              <Button onClick={() => navigate('/')}>Back to Home</Button>
              <Button variant="outline" onClick={() => setSubmitted(false)}>Send Another</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        pageKey="contact"
        seo={companyInfo?.seo}
        defaults={{
          title: 'Contact Ecoway Cabs | Book Corporate & Intercity Transportation',
          description: 'Contact Ecoway Cabs for corporate cab services, employee transportation, and intercity travel across India. Call or send a message — we respond quickly.',
          keywords: 'contact Ecoway Cabs, book cab, corporate transportation enquiry, cab booking phone, cab service contact',
        }}
        path="/contact"
      />
      <section className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-blue-100">{companyInfo?.contact_subtitle || 'Get in touch with our team'}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Call Us</h3>
                  <a href={`tel:${companyInfo?.contact_phone}`} className="text-blue-600 hover:text-blue-700">{companyInfo?.contact_phone || '+91-9876543210'}</a>
                  <p className="text-sm text-gray-500 mt-2">Mon-Sun, 24/7 Available</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Email Us</h3>
                  <a href={`mailto:${companyInfo?.contact_email}`} className="text-blue-600 hover:text-blue-700 break-all">{companyInfo?.contact_email || 'info@ecowaycabs.com'}</a>
                  <p className="text-sm text-gray-500 mt-2">We'll respond within 24 hours</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Visit Us</h3>
                  <p className="text-gray-700">{companyInfo?.address || '123 Business Park, MG Road, Bangalore - 560001'}</p>
                  <p className="text-sm text-gray-500 mt-2">Office Hours: 9 AM - 6 PM</p>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Phone *</Label>
                        <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required />
                      </div>
                      <div>
                        <Label>Subject *</Label>
                        <Input value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required />
                      </div>
                    </div>

                    <div>
                      <Label>Message *</Label>
                      <Textarea rows={6} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required />
                    </div>

                    <Button type="submit" className="w-full" size="lg" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                    
                    <p className="text-xs text-center text-gray-500 mt-2">
                      Protected by reCAPTCHA
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
